import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IScimGroupDetail, IScimUser } from '@shared/models/scim-admin';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { ScimAdminService } from '../scim-admin.service';

interface IResolvedMember {
	userId: string;
	name: string;
	userExists: boolean;
}

@Component({
	selector: 'app-scim-group-detail-dialog',
	standalone: false,
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './scim-group-detail-dialog.component.html',
})
export class ScimGroupDetailDialogComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	resolvedMembers: IResolvedMember[] = [];
	isLoadingMembers = false;
	isRemoving = false;
	private hasChanges = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public group: IScimGroupDetail,
		public dialogRef: MatDialogRef<ScimGroupDetailDialogComponent>,
		private scimAdminService: ScimAdminService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		if (this.hasMembers) {
			this.loadMemberNames();
		}
	}

	get hasMembers(): boolean {
		return this.group.members && this.group.members.length > 0;
	}

	private loadMemberNames(): void {
		this.isLoadingMembers = true;
		this.scimAdminService
			.getUsers()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (users) => {
					const userMap = new Map<string, IScimUser>();
					users.forEach((u) => userMap.set(u.id, u));

					this.resolvedMembers = this.group.members.map((member) => {
						const user = userMap.get(member.userId);
						const name = user ? [user.name?.givenName, user.name?.familyName].filter(Boolean).join(' ') || user.displayName || user.userName || '' : '';
						return {
							userId: member.userId,
							name,
							userExists: !!user,
						};
					});
					this.isLoadingMembers = false;
				},
				error: () => {
					this.resolvedMembers = this.group.members.map((member) => ({
						userId: member.userId,
						name: '',
						userExists: false,
					}));
					this.isLoadingMembers = false;
				},
			});
	}

	removeMember(member: IResolvedMember): void {
		this.isRemoving = true;
		this.scimAdminService
			.patchGroupRemoveMembers(this.group.id, [member.userId])
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (updatedGroup) => {
					this.group = updatedGroup;
					this.resolvedMembers = this.resolvedMembers.filter((m) => m.userId !== member.userId);
					this.isRemoving = false;
					this.hasChanges = true;
					this.notificationService.notify(['scim-administration.groups.detail.removeMember.success.header'], ['scim-administration.groups.detail.removeMember.success.content'], 'success');
				},
				error: () => {
					this.isRemoving = false;
					this.notificationService.notify(['scim-administration.groups.detail.removeMember.error.header'], ['scim-administration.groups.detail.removeMember.error.content'], 'error');
				},
			});
	}

	close(): void {
		this.dialogRef.close(this.hasChanges ? this.group : undefined);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
